import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { MessageSquare, Send, User, Store, ChevronLeft, Clock } from 'lucide-react';
import { toast } from 'sonner';

const MessagingPanel = ({ userType, isRep = false }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations();
    
    // Poll for new conversations every 10 seconds
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, []);

  // Poll for new messages when a conversation is selected
  useEffect(() => {
    if (selectedConversation) {
      const interval = setInterval(() => fetchMessages(selectedConversation.id, true), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation?.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const endpoint = isRep ? `${API}/messages/conversations/rep` : `${API}/messages/conversations`;
      const res = await axios.get(endpoint);
      setConversations(res.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  };

  const fetchMessages = async (conversationId, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const endpoint = isRep 
        ? `${API}/messages/conversations/${conversationId}/rep`
        : `${API}/messages/conversations/${conversationId}`;
      const res = await axios.get(endpoint);
      setMessages(res.data.messages);
      
      // Update unread count in conversation list
      if (!silent) {
        fetchConversations();
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      if (!silent) toast.error('Failed to load messages');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sendingMessage) return;

    setSendingMessage(true);
    try {
      const endpoint = isRep
        ? `${API}/messages/conversations/${selectedConversation.id}/rep`
        : `${API}/messages/conversations/${selectedConversation.id}`;
      
      await axios.post(endpoint, { content: newMessage.trim() });
      setNewMessage('');
      fetchMessages(selectedConversation.id);
      fetchConversations(); // Update last message preview
      inputRef.current?.focus();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getUnreadCount = (conversation) => {
    if (userType === 'buyer') {
      return conversation.unread_buyer || 0;
    }
    return conversation.unread_seller || 0;
  };

  const getConversationName = (conversation) => {
    if (userType === 'buyer') {
      return conversation.booth_company_name || conversation.booth?.company_name || 'Exhibitor';
    }
    return conversation.buyer_name || 'Buyer';
  };

  const getConversationAvatar = (conversation) => {
    if (userType === 'buyer') {
      return <Store className="h-5 w-5" />;
    }
    return <User className="h-5 w-5" />;
  };

  return (
    <div className="h-[600px] flex border border-slate-200 rounded-lg overflow-hidden bg-white">
      {/* Conversation List */}
      <div className={`${selectedConversation ? 'hidden md:flex' : 'flex'} w-full md:w-80 flex-col border-r border-slate-200`}>
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Messages
          </h3>
        </div>
        
        <ScrollArea className="flex-1">
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No conversations yet</p>
              {userType === 'buyer' && (
                <p className="text-xs mt-1">Start a conversation from an exhibitor&apos;s booth</p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {conversations.map((conversation) => {
                const unread = getUnreadCount(conversation);
                const isSelected = selectedConversation?.id === conversation.id;
                
                return (
                  <div
                    key={conversation.id}
                    className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                    onClick={() => selectConversation(conversation)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isSelected ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600'}`}>
                        {getConversationAvatar(conversation)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`font-medium text-sm truncate ${unread > 0 ? 'text-slate-900' : 'text-slate-700'}`}>
                            {getConversationName(conversation)}
                          </span>
                          <span className="text-xs text-slate-500 ml-2 flex-shrink-0">
                            {formatTime(conversation.last_message_at)}
                          </span>
                        </div>
                        {/* Show Exhibit/Show name */}
                        {conversation.event_name && (
                          <p className="text-xs text-primary/70 truncate mt-0.5">
                            {conversation.event_name}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-1">
                          <p className={`text-xs truncate ${unread > 0 ? 'text-slate-700 font-medium' : 'text-slate-500'}`}>
                            {conversation.last_message_preview || 'No messages yet'}
                          </p>
                          {unread > 0 && (
                            <Badge className="ml-2 bg-blue-600 text-white text-xs px-2 py-0 min-w-[20px] flex-shrink-0">
                              {unread}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Message Thread */}
      <div className={`${selectedConversation ? 'flex' : 'hidden md:flex'} flex-1 flex-col`}>
        {selectedConversation ? (
          <>
            {/* Header */}
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                className="md:hidden"
                onClick={() => setSelectedConversation(null)}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                {getConversationAvatar(selectedConversation)}
              </div>
              <div className="flex-1">
                <h4 className="font-semibold">{getConversationName(selectedConversation)}</h4>
                {selectedConversation.event_name && (
                  <p className="text-xs text-primary/70">{selectedConversation.event_name}</p>
                )}
                {userType !== 'buyer' && selectedConversation.buyer_email && (
                  <p className="text-xs text-slate-500">{selectedConversation.buyer_email}</p>
                )}
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                  <MessageSquare className="h-12 w-12 mb-3 text-slate-300" />
                  <p>No messages yet</p>
                  <p className="text-sm">Send a message to start the conversation</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message) => {
                    const isOwn = (userType === 'buyer' && message.sender_type === 'buyer') ||
                                  (userType === 'seller' && (message.sender_type === 'seller' || message.sender_type === 'rep')) ||
                                  (isRep && (message.sender_type === 'seller' || message.sender_type === 'rep'));
                    
                    // Format sender label with role
                    const getSenderLabel = () => {
                      const name = message.sender_name || 'Unknown';
                      if (message.sender_type === 'buyer') {
                        return `${name} (Buyer)`;
                      } else if (message.sender_type === 'rep') {
                        return `${name} (Rep)`;
                      } else if (message.sender_type === 'seller') {
                        return `${name} (Exhibitor)`;
                      }
                      return name;
                    };
                    
                    return (
                      <div
                        key={message.id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[75%] ${isOwn ? 'order-2' : 'order-1'}`}>
                          {/* Sender name above message */}
                          <div className={`mb-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                            <span className={`text-xs font-medium ${
                              message.sender_type === 'buyer' 
                                ? 'text-blue-600' 
                                : message.sender_type === 'rep' 
                                  ? 'text-purple-600' 
                                  : 'text-orange-600'
                            }`}>
                              {getSenderLabel()}
                            </span>
                          </div>
                          <div
                            className={`px-4 py-2 rounded-2xl ${
                              isOwn
                                ? 'bg-blue-600 text-white rounded-br-md'
                                : 'bg-slate-100 text-slate-900 rounded-bl-md'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                          </div>
                          <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            <span className="text-xs text-slate-400">
                              {formatTime(message.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-slate-200 bg-white">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  disabled={sendingMessage}
                  className="flex-1"
                />
                <Button type="submit" disabled={!newMessage.trim() || sendingMessage}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-500 bg-slate-50">
            <MessageSquare className="h-16 w-16 mb-4 text-slate-300" />
            <p className="font-medium">Select a conversation</p>
            <p className="text-sm">Choose from your existing conversations</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagingPanel;
